import { db } from '../config/firebase';
import { getLead, updateLead } from './lead.service';
import { CalendarService } from './calendar.service';
import { formatTime, formatTimeRange } from '../utils/dateUtils';
import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  query,
  where,
  orderBy,
  runTransaction,
  Timestamp
} from 'firebase/firestore';
import { Appointment } from '../types/schedule.types';

// Thrown by createAppointment when the requested slot is at capacity.
// The booking modal catches this and shows a friendly toast.
export class SlotFullError extends Error {
  constructor(message: string = 'This time slot is full') {
    super(message);
    this.name = 'SlotFullError';
  }
}

// Build the deterministic doc ID for a slot lock.
// One lock doc per (date, userId, startTime). Storing the running booking
// count here lets us reject overflow inside a Firestore transaction without
// having to query the appointments collection from inside the transaction
// (transactions only support get-by-ref, not queries).
// Capacity is tracked PER campus (schoolSegmentId), so the same (date,userId,
// startTime) at different campuses gets independent locks — 3pm at Metro North
// doesn't block 3pm at Maryvale. Empty campus = project-wide ('all').
const slotLockId = (date: string, userId: string, startTime: string, campus?: string): string =>
  `${date}_${userId}_${startTime}_${campus || 'all'}`;

// Decrement the slot lock count for a given (date,userId,startTime). Used
// when an appointment is cancelled, no-shown, or hard-deleted so the slot
// frees up. Idempotent in the sense that we never go below zero.
async function releaseSlotLock(
  date: string,
  userId: string,
  startTime: string,
  campus?: string
): Promise<void> {
  const lockRef = doc(db, 'slotLocks', slotLockId(date, userId, startTime, campus));
  try {
    await runTransaction(db, async (tx) => {
      const snap = await tx.get(lockRef);
      if (!snap.exists()) return;
      const currentCount = snap.data().count || 0;
      const nextCount = Math.max(0, currentCount - 1);
      tx.set(
        lockRef,
        { count: nextCount, updatedAt: Timestamp.now() },
        { merge: true }
      );
    });
  } catch (err) {
    // Releasing should never block the user-facing action.
    console.error('Failed to release slot lock', { date, userId, startTime, err });
  }
}

// Notification types for appointments
interface AppointmentNotification {
  appointmentId: string;
  type: 'tour' | 'phone' | 'meeting';
  leadName: string;
  date: string;
  time: string;
  clientUserName: string;
  clientUserEmail: string;
  fieldUserName?: string;
  fieldUserEmail?: string;
  location?: string;
}

export const appointmentService = {
  // Create appointment with notifications and calendar sync
  // Flow: field_user schedules from client_user's availability
  // Notifications go to: field_user, company users (provider_admin), client_user
  // Calendar push: client_user who is scheduled with
  async createAppointment(
    appointmentData: Omit<Appointment, 'id' | 'createdAt'>,
    options?: {
      createdByUserId?: string;
      createdByUserName?: string;
      skipNotifications?: boolean;
      slotCapacity?: number; // Max parents per slot. Defaults to 1.
    }
  ): Promise<string> {
    try {
      const appointmentRef = doc(collection(db, 'appointments'));
      const appointment = {
        ...appointmentData,
        id: appointmentRef.id,
        createdAt: Timestamp.now(),
        createdBy: options?.createdByUserId || '',
        createdByName: options?.createdByUserName || ''
      };

      // Capacity-enforced create. Uses a per-slot lock doc so two
      // simultaneous bookings can't both pass a stale check.
      const capacity = Math.max(1, options?.slotCapacity ?? 1);
      const lockRef = doc(
        db,
        'slotLocks',
        slotLockId(appointmentData.date, appointmentData.userId, appointmentData.startTime, appointmentData.schoolSegmentId)
      );

      await runTransaction(db, async (tx) => {
        const lockSnap = await tx.get(lockRef);
        const currentCount = lockSnap.exists() ? (lockSnap.data().count || 0) : 0;

        if (currentCount >= capacity) {
          throw new SlotFullError();
        }

        tx.set(
          lockRef,
          {
            date: appointmentData.date,
            userId: appointmentData.userId,
            startTime: appointmentData.startTime,
            endTime: appointmentData.endTime,
            count: currentCount + 1,
            capacity,
            updatedAt: Timestamp.now(),
          },
          { merge: true }
        );

        tx.set(appointmentRef, appointment);
      });

      // Send notifications and sync calendar (non-blocking)
      if (!options?.skipNotifications) {
        this.sendAppointmentNotifications(appointment).catch(err => {
          console.error('Error sending appointment notifications:', err);
        });
      }

      return appointmentRef.id;
    } catch (error) {
      console.error('Error creating appointment:', error);
      throw error;
    }
  },

  // Send notifications to field_user, company users, and client_user
  // Also syncs to client_user's connected calendars
  async sendAppointmentNotifications(appointment: Appointment & { createdBy?: string; createdByName?: string }): Promise<void> {
    try {
      // Get client_user info (the one being scheduled with)
      const clientUserDoc = await getDoc(doc(db, 'users', appointment.userId));
      const clientUser = clientUserDoc.exists() ? clientUserDoc.data() : null;

      // Get field_user info (the one who created the appointment)
      let fieldUser = null;
      if (appointment.createdBy) {
        const fieldUserDoc = await getDoc(doc(db, 'users', appointment.createdBy));
        fieldUser = fieldUserDoc.exists() ? fieldUserDoc.data() : null;
      }

      // Get project to find company users (provider_admin)
      const projectDoc = await getDoc(doc(db, 'projects', appointment.projectId));
      const project = projectDoc.exists() ? projectDoc.data() : null;

      // Build notification data
      const projectTimezone = project?.settings?.timezone || '';
      const formattedTime = formatTime(appointment.startTime, projectTimezone);
      const formattedRange = formatTimeRange(appointment.startTime, appointment.endTime, projectTimezone);

      const notificationData: AppointmentNotification = {
        appointmentId: appointment.id,
        type: appointment.eventType,
        leadName: appointment.leadName || 'Unknown',
        date: appointment.date,
        time: formattedRange,
        clientUserName: clientUser?.firstName ? `${clientUser.firstName} ${clientUser.lastName || ''}` : 'Unknown',
        clientUserEmail: clientUser?.email || '',
        fieldUserName: fieldUser ? `${fieldUser.firstName} ${fieldUser.lastName || ''}` : appointment.createdByName,
        fieldUserEmail: fieldUser?.email || ''
      };

      // 1. Create notification records in Firestore for in-app notifications
      const notificationsToCreate = [];

      // Notification for client_user
      if (clientUser) {
        notificationsToCreate.push({
          userId: appointment.userId,
          type: 'appointment_scheduled',
          title: `New ${appointment.eventType === 'tour' ? 'Tour' : 'Call'} Scheduled`,
          message: `${notificationData.fieldUserName} scheduled a ${appointment.eventType} with ${notificationData.leadName} for ${appointment.date} at ${formattedTime}`,
          data: notificationData,
          read: false,
          createdAt: Timestamp.now()
        });
      }

      // Notification for field_user (if different from client_user)
      if (appointment.createdBy && appointment.createdBy !== appointment.userId) {
        notificationsToCreate.push({
          userId: appointment.createdBy,
          type: 'appointment_confirmed',
          title: `${appointment.eventType === 'tour' ? 'Tour' : 'Call'} Confirmed`,
          message: `${appointment.eventType === 'tour' ? 'Tour' : 'Call'} with ${notificationData.leadName} scheduled for ${appointment.date} at ${formattedTime} with ${notificationData.clientUserName}`,
          data: notificationData,
          read: false,
          createdAt: Timestamp.now()
        });
      }

      // Notification for provider_admin and provider_user
      if (project?.assignedUsers) {
        const providerUsers = project.assignedUsers.filter(
          (u: any) => u.role === 'provider_admin' || u.role === 'provider_user'
        );
        for (const provider of providerUsers) {
          // Don't duplicate if they created the appointment
          if (provider.userId === appointment.createdBy) continue;

          notificationsToCreate.push({
            userId: provider.userId,
            type: 'appointment_scheduled',
            title: `New ${appointment.eventType === 'tour' ? 'Tour' : 'Call'} Scheduled`,
            message: `${notificationData.fieldUserName} scheduled a ${appointment.eventType} with ${notificationData.leadName} for ${appointment.date} at ${formattedTime}`,
            data: notificationData,
            read: false,
            createdAt: Timestamp.now()
          });
        }
      }

      // Save all notifications
      for (const notification of notificationsToCreate) {
        const notifRef = doc(collection(db, 'notifications'));
        await setDoc(notifRef, { id: notifRef.id, ...notification });
      }

      // 2. Push to client_user's connected calendars
      if (clientUser) {
        const startDateTime = new Date(`${appointment.date}T${appointment.startTime}`);
        const endDateTime = new Date(`${appointment.date}T${appointment.endTime}`);

        await CalendarService.syncAppointmentToCalendars(appointment.userId, {
          id: appointment.id,
          title: `${appointment.eventType === 'tour' ? 'Tour' : 'Call'}: ${notificationData.leadName}`,
          description: `${appointment.eventType === 'tour' ? 'Tour' : 'Follow-up call'} with ${notificationData.leadName}\nPhone: ${appointment.phone || 'N/A'}\nScheduled by: ${notificationData.fieldUserName}`,
          startDate: startDateTime,
          endDate: endDateTime,
          eventType: appointment.eventType,
          attendees: appointment.phone ? [{ email: '', name: notificationData.leadName }] : undefined
        });
      }

      // TODO: integrate SendGrid for email notifications
    } catch (error) {
      console.error('Error sending appointment notifications:', error);
      // Don't throw - notifications failing shouldn't fail the appointment creation
    }
  },

  async getAppointmentsByProject(projectId: string): Promise<Appointment[]> {
    const q = query(
      collection(db, 'appointments'),
      where('projectId', '==', projectId),
      orderBy('date', 'asc'),
      orderBy('startTime', 'asc')
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as Appointment));
  },
    async updateAppointment(appointmentId: string, updates: Partial<Appointment>): Promise<void> {
    try {
      // Get the appointment first to get leadId
      const appointmentDoc = await getDoc(doc(db, 'appointments', appointmentId));
      if (!appointmentDoc.exists()) {
        throw new Error('Appointment not found');
      }
      
      const appointment = appointmentDoc.data() as Appointment;
      
      // Update the appointment
      await updateDoc(doc(db, 'appointments', appointmentId), {
        ...updates,
        updatedAt: Timestamp.now()
      });

      // If the appointment is being cancelled or marked no-show, free up the
      // slot lock so the slot becomes bookable again.
      const releasingStatuses: Array<typeof appointment.status> = ['cancelled', 'no-show'];
      const wasReleasing = releasingStatuses.includes(appointment.status);
      const nowReleasing = updates.status ? releasingStatuses.includes(updates.status) : false;
      if (nowReleasing && !wasReleasing) {
        await releaseSlotLock(appointment.date, appointment.userId, appointment.startTime, appointment.schoolSegmentId);
      }

      // If status is being updated, also update the lead notes
      if (updates.status && updates.status !== appointment.status) {
        const lead = await getLead(appointment.leadId);
        if (lead) {
          const projDoc = await getDoc(doc(db, 'projects', appointment.projectId));
          const tz = projDoc.exists() ? projDoc.data()?.settings?.timezone || '' : '';
          const statusNote = `\nAppointment ${updates.status} on ${appointment.date} at ${formatTime(appointment.startTime, tz)}`;
          await updateLead(appointment.leadId, {
            notes: (lead.notes || '') + statusNote
          });
        }
      }
      
    } catch (error) {
      console.error('Error updating appointment:', error);
      throw error;
    }
  },
  // Move an existing appointment to a new slot (date/time and/or host).
  // Releases the old slot lock and acquires the new one atomically and
  // capacity-enforced (per campus), so a reschedule can't overflow a full
  // destination slot — throws SlotFullError if it would. The lead's campus
  // (schoolSegmentId) is invariant on a reschedule, so both locks key off the
  // appointment's existing campus. Appends a note to the lead's trail. The
  // updated confirmation email is sent by the `onAppointmentUpdated` cloud
  // trigger, which fires on the date/startTime/endTime change.
  async rescheduleAppointment(
    appointmentId: string,
    newSlot: {
      date: string;
      startTime: string;
      endTime: string;
      userId: string;
      userName?: string;
      scheduleId?: string;
      eventType?: 'tour' | 'phone' | 'meeting';
    },
    options?: { slotCapacity?: number; changedByName?: string }
  ): Promise<void> {
    const apptRef = doc(db, 'appointments', appointmentId);
    const apptSnap = await getDoc(apptRef);
    if (!apptSnap.exists()) {
      throw new Error('Appointment not found');
    }
    const appt = apptSnap.data() as Appointment;

    const campus = appt.schoolSegmentId;
    const oldLockRef = doc(db, 'slotLocks', slotLockId(appt.date, appt.userId, appt.startTime, campus));
    const newLockRef = doc(db, 'slotLocks', slotLockId(newSlot.date, newSlot.userId, newSlot.startTime, campus));
    const capacity = Math.max(1, options?.slotCapacity ?? 1);
    const sameSlot = oldLockRef.id === newLockRef.id;
    // A cancelled/no-show appointment already released its lock; only decrement
    // the old lock if this appointment is still holding one.
    const oldHoldsLock = appt.status === 'scheduled' || appt.status === 'completed';

    const apptUpdate: Partial<Appointment> = {
      date: newSlot.date,
      startTime: newSlot.startTime,
      endTime: newSlot.endTime,
      userId: newSlot.userId,
      ...(newSlot.scheduleId ? { scheduleId: newSlot.scheduleId } : {}),
      ...(newSlot.eventType ? { eventType: newSlot.eventType } : {}),
    };

    await runTransaction(db, async (tx) => {
      // All reads must precede all writes in a Firestore transaction.
      const newLockSnap = await tx.get(newLockRef);
      const oldLockSnap = sameSlot ? newLockSnap : await tx.get(oldLockRef);

      if (!sameSlot) {
        // The destination count excludes this appointment (it isn't there yet).
        const newCount = newLockSnap.exists() ? (newLockSnap.data().count || 0) : 0;
        if (newCount >= capacity) {
          throw new SlotFullError();
        }

        // Release the old slot.
        if (oldHoldsLock) {
          const oldCount = oldLockSnap.exists() ? (oldLockSnap.data().count || 0) : 0;
          tx.set(
            oldLockRef,
            { count: Math.max(0, oldCount - 1), updatedAt: Timestamp.now() },
            { merge: true }
          );
        }

        // Acquire the new slot.
        tx.set(
          newLockRef,
          {
            date: newSlot.date,
            userId: newSlot.userId,
            startTime: newSlot.startTime,
            endTime: newSlot.endTime,
            count: newCount + 1,
            capacity,
            updatedAt: Timestamp.now(),
          },
          { merge: true }
        );
      }

      tx.update(apptRef, { ...apptUpdate, updatedAt: Timestamp.now() });
    });

    // Append a reschedule note to the lead's trail (non-blocking on failure).
    try {
      const lead = await getLead(appt.leadId);
      if (lead) {
        const projDoc = await getDoc(doc(db, 'projects', appt.projectId));
        const tz = projDoc.exists() ? projDoc.data()?.settings?.timezone || '' : '';
        const who = options?.changedByName ? ` by ${options.changedByName}` : '';
        const note = `\nAppointment rescheduled${who} — was ${appt.date} at ${formatTime(appt.startTime, tz)}, now ${newSlot.date} at ${formatTime(newSlot.startTime, tz)}`;
        await updateLead(appt.leadId, { notes: (lead.notes || '') + note });
      }
    } catch (err) {
      console.error('Failed to append reschedule note to lead', err);
    }
  },

  // Delete an appointment and release its slot lock so the slot frees up.
  async deleteAppointment(appointmentId: string): Promise<void> {
    const ref = doc(db, 'appointments', appointmentId);
    const snap = await getDoc(ref);
    if (!snap.exists()) return;
    const apt = snap.data() as Appointment;
    await updateDoc(ref, { status: 'cancelled', updatedAt: Timestamp.now() });
    await releaseSlotLock(apt.date, apt.userId, apt.startTime, apt.schoolSegmentId);
  },

  // Internal-only export so other services (e.g. lead.service.deleteLead)
  // can release locks during cascading deletes.
  _releaseSlotLock: releaseSlotLock,

  async getAppointmentsByUser(userId: string): Promise<Appointment[]> {
    const q = query(
      collection(db, 'appointments'),
      where('userId', '==', userId),
      orderBy('date', 'asc'),
      orderBy('startTime', 'asc')
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as Appointment));
  }
};
